import { NoDataMessage } from '@/components';
import { Typography, theme } from 'antd';
import DataTableEcho from '@/components/tables/DataTable/DataTableEcho';
import type { Campaign, CampaignsApiResponse, CampaignsFilter, SortField } from '@/types';
import { useMemo, useRef } from 'react';
import TableColumns from './TableColumns';
import TableContentEcho from './TableContentEcho';
import { buildCampaignsEchoColumns } from './buildCampaignsEchoColumns';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import { usePageMetadata } from '@/hooks';
import { debounce } from 'lodash-es';
import { getCampaignsTablePaginateClickedEventByPageType } from '@/utils/mixpanelUtils/pageTypeHelpers';

const CampaignsTableEcho = ({
  data,
  filters,
}: {
  data: CampaignsApiResponse;
  filters: CampaignsFilter;
}) => {
  const { page: pageType } = usePageMetadata();
  const { token } = theme.useToken();

  if (!pageType) throw new Error('Page type is required');

  const columnSortEventName =
    pageType === 'earnings'
      ? MIXPANEL_EVENT.earnings.campaignsTable.sortColumnClicked
      : MIXPANEL_EVENT.payouts.campaignsTable.sortColumnClicked;

  const debouncedTrackSort = useRef(
    debounce(
      (sortField: SortField) =>
        mixpanelTrack(columnSortEventName, {
          widget: RevenueDashboardWidget.Campaigns,
          page: pageType,
          column: sortField.key,
          order: sortField.direction,
        }),
      1000
    )
  );

  const allCampaigns = useMemo(() => data?.all_campaigns || [], [data]);
  const filteredData = useMemo(() => {
    const { showId, advertiserName, campaignType } = filters;

    const activeFilters = [
      {
        isActive: showId.length > 0,
        matches: (campaign: Campaign) => showId.includes(campaign.show.id),
      },
      {
        isActive: advertiserName.length > 0,
        matches: (campaign: Campaign) => advertiserName.includes(campaign.brand),
      },
      {
        isActive: campaignType.length > 0,
        matches: (campaign: Campaign) => campaignType.includes(campaign.type.name),
      },
    ];

    return allCampaigns?.filter((campaign: Campaign) => {
      // If no filters are active, show all campaigns
      const hasActiveFilters = activeFilters.some((filter) => filter.isActive);

      if (!hasActiveFilters) {
        return true;
      }
      // Check if campaign matches all active filters
      return activeFilters.every((filter) => !filter.isActive || filter.matches(campaign));
    });
  }, [filters, allCampaigns]);

  const handleSort = (sortField: SortField) => {
    debouncedTrackSort.current(sortField);
  };

  const handlePageChange = (page: number) => {
    mixpanelTrack(getCampaignsTablePaginateClickedEventByPageType(pageType), {
      widget: RevenueDashboardWidget.Campaigns,
      page: pageType,
      pageNumber: page,
    });
  };

  const campaignsEchoColumns = useMemo(
    () => buildCampaignsEchoColumns(TableColumns(), token),
    [token]
  );

  if (allCampaigns.length === 0) {
    return <NoDataMessage dataType="campaigns" />;
  }

  return (
    <DataTableEcho
      columns={campaignsEchoColumns}
      dataSource={TableContentEcho(filteredData)}
      tableProps={{ scroll: { x: true } }}
      initialState={{
        sorting: {
          key: 'amount',
          direction: 'desc',
        },
      }}
      pagination={{
        summary: (fromPage, toPage, total) => (
          <Typography.Text>
            Showing {fromPage}-{toPage} of {total} items
          </Typography.Text>
        ),
      }}
      tableWrapperClassName="min-h-[400px]"
      onSort={handleSort}
      onPageChange={handlePageChange}
    />
  );
};

export default CampaignsTableEcho;
